import React, { useEffect, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Label,
} from '../ui'
import { LoadingSpinner } from '../ui/loading-states'
import {
  useUpdateMonthlyStaffingLimit,
  type ShiftLimit,
} from '../../hooks/useStaffingLimits'

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
]

interface EditShiftLimitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  limit: ShiftLimit | null
}

/**
 * Ручная правка лимита смен. После сохранения source становится 'manual',
 * и автосинк штатки 1С больше не перезаписывает это значение.
 */
export function EditShiftLimitDialog({ open, onOpenChange, limit }: EditShiftLimitDialogProps) {
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const updateLimit = useUpdateMonthlyStaffingLimit()

  useEffect(() => {
    if (open && limit) {
      setValue(String(limit.max_shifts))
      setError('')
      updateLimit.reset()
    }
  }, [open, limit])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!limit) return

    const parsed = parseInt(value, 10)
    if (!parsed || parsed < 1) {
      setError('Лимит смен должен быть не менее 1')
      return
    }

    try {
      await updateLimit.mutateAsync({ id: limit.id, data: { max_shifts: parsed } })
      onOpenChange(false)
    } catch {
      // ошибка показывается ниже из updateLimit.error
    }
  }

  const handleClose = () => {
    if (!updateLimit.isPending) onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Изменить лимит смен</DialogTitle>
          <DialogDescription>
            {limit
              ? `${limit.position?.name || 'Должность не указана'} — ${limit.department?.name || 'вся организация'}, ${MONTH_NAMES[limit.month - 1]} ${limit.year}`
              : ''}
          </DialogDescription>
          {!updateLimit.isPending && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="max_shifts">Максимум смен в месяц *</Label>
            <Input
              id="max_shifts"
              type="number"
              min="1"
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                setError('')
              }}
              disabled={updateLimit.isPending}
              className={error ? 'border-red-500' : ''}
            />
            {error && (
              <div className="flex items-center mt-1 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                {error}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              После ручного изменения лимит перестаёт обновляться автосинком штатного расписания 1С —
              вернуть автоматический расчёт можно только пересчётом с перезаписью ручных значений.
            </p>
          </div>

          {updateLimit.error && (
            <div className="flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(updateLimit.error as { message?: string })?.message ||
                  'Не удалось сохранить лимит'}
              </span>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={updateLimit.isPending}>
              Отменить
            </Button>
            <Button type="submit" disabled={updateLimit.isPending} className="min-w-[100px]">
              {updateLimit.isPending ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Сохранение...
                </>
              ) : (
                'Сохранить'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
